import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useEffect, useState } from 'react';
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { checkoutReservationURL, GetBookingInvoiceDetailsURL, getReservationURL, record_checkout_for_revenue } from "../../../utils/urls";
import reservationTrendService from "./reservation-trend-service";
import PaymentModal from "./booking_invoices/PaymentModal";
import RecordPaymentDialog from "./booking_invoices/RecordPaymentDialog";

const CheckoutModal = ({
  visible,
  onHide,
  reservation,
  onSuccess
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [checkoutDate, setCheckoutDate] = useState(new Date());
  const [checkoutNotes, setCheckoutNotes] = useState('');
  const [finalAmount, setFinalAmount] = useState(0);
  const [additionalChargesTotal, setAdditionalChargesTotal] = useState(0);
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [newCharge, setNewCharge] = useState({
    description: '',
    amount: 0,
    reason: ''
  });
  const [isManagedByLandlord, setIsManagedByLandlord] = useState(false);
  const [currentReservation, setCurrentReservation] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [balanceDue, setBalanceDue] = useState(0);
  const [chargesInitialized, setChargesInitialized] = useState(false);
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0);
  const [mpesaModalVisible, setMpesaModalVisible] = useState(false);
  const [recordPaymentModalVisible, setRecordPaymentModalVisible] = useState(false);
  const [checkoutFacilityId, setCheckoutFacilityId] = useState(null);

  useEffect(() => {
    if (!visible) return;
    getItem("selectedFacilityId").then(setCheckoutFacilityId);
  }, [visible]);

  // Fetch booking property details to determine management type
  useEffect(() => {
    const fetchBookingPropertyDetails = async () => {
      const reservationToUse = currentReservation || reservation;
      if (!reservationToUse?.bookingPropertyId) return;

      try {
        const facilityId = await getItem("selectedFacilityId");
        const response = await makeRequest2(
          `/api/booking-properties/${facilityId}/${reservationToUse.bookingPropertyId}`,
          "GET"
        );

        if (response.success) {
          const bookingProperty = response.data?.data || response.data;
          setIsManagedByLandlord(bookingProperty.managedByLandlord || false);
        }
      } catch (error) {
        console.error("Error fetching booking property:", error);
        // Default to property manager if we can't determine
        setIsManagedByLandlord(false);
      }
    };

    if (visible && reservation) {
      fetchBookingPropertyDetails();
    }
  }, [visible, reservation, currentReservation]);

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!reservation || !visible) return;

      const reservationToUse = currentReservation || reservation;
      const invoiceLookupId = reservationToUse.invoiceId || reservationToUse.invoiceNumber || reservationToUse.invoice?.invoiceId;
      if (!invoiceLookupId) {
        setInvoiceDetails(null);
        return;
      }

      try {
        setInvoiceLoading(true);
        const facilityId = await getItem("selectedFacilityId");
        const response = await makeRequest2(
          `${GetBookingInvoiceDetailsURL}/${facilityId}/${invoiceLookupId}`,
          "GET"
        );

        if (response.success) {
          setInvoiceDetails(response.data?.data || response.data);
        } else {
          setInvoiceDetails(null);
        }
      } catch (error) {
        console.error("Error fetching booking invoice:", error);
        setInvoiceDetails(null);
      } finally {
        setInvoiceLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [visible, reservation, currentReservation, invoiceRefreshKey]);

  const refreshReservation = async () => {
    if (!reservation?._id) return;

    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getReservationURL}/${facilityId}/${reservation._id}`,
        "GET"
      );

      if (response.success) {
        const data = response.data?.data || response.data;
        setCurrentReservation(data || reservation);
      } else {
        setCurrentReservation(reservation);
      }
    } catch (error) {
      console.error("Error fetching reservation details:", error);
      setCurrentReservation(reservation);
    }
  };

  useEffect(() => {
    if (!visible || !reservation?._id) return;
    refreshReservation();
  }, [visible, reservation]);

  useEffect(() => {
    if (!visible) {
      setChargesInitialized(false);
      setCurrentReservation(null);
    }
  }, [visible]);

  // Initialize data when reservation changes
  useEffect(() => {
    const reservationToUse = currentReservation || reservation;
    if (reservationToUse) {
      // For landlord managed, no initial amount; for property manager, use total amount
      setFinalAmount(isManagedByLandlord ? 0 : (reservationToUse.totalAmount || 0));

      // Reset other fields
      setCheckoutNotes('');
      const existingCharges = Array.isArray(reservationToUse.additionalCheckoutCharges)
        ? reservationToUse.additionalCheckoutCharges.map((charge) => ({
          ...charge,
          id: charge.id || charge._id || `${Date.now()}-${Math.random()}`
        }))
        : [];
      setAdditionalCharges(existingCharges);
      setAdditionalChargesTotal(
        existingCharges.reduce((sum, charge) => sum + (Number(charge.amount) || 0), 0)
      );
      setChargesInitialized(true);
      setNewCharge({
        description: '',
        amount: 0,
        reason: ''
      });
      setPaymentMethod(reservationToUse.paymentMethod || '');
      setPaymentAmount(0);
      setPaymentNotes('');
      setReceiptNumber('');
      setPaymentDate(new Date());

      // Set checkout date to today
      setCheckoutDate(new Date());
    }
  }, [reservation, currentReservation, isManagedByLandlord]);

  const computeBalanceDue = () => {
    const chargesTotal = additionalCharges.reduce((sum, charge) => sum + (Number(charge.amount) || 0), 0);
    const invoiceBalance = invoiceDetails?.calculatedData?.balanceRemaining || 0;
    const invoiceTotal = Number(invoiceDetails?.totalAmount || 0);
    const baseTotal = Number(reservation?.totalAmount || invoiceDetails?.basePrice || 0);
    const includesCharges = invoiceTotal >= (baseTotal + chargesTotal - 0.01);
    const updatedBalance = Math.max(0, includesCharges ? invoiceBalance : (invoiceBalance + chargesTotal));

    return {
      chargesTotal,
      invoiceBalance,
      updatedBalance,
      includesCharges
    };
  };

  // Update final amount when additional charges change
  useEffect(() => {
    const { chargesTotal, updatedBalance } = computeBalanceDue();

    if (isManagedByLandlord) {
      // For landlord managed: only additional charges
      setFinalAmount(chargesTotal);
    } else {
      // For property manager: base amount + additional charges
      setFinalAmount(Number(reservation?.totalAmount || 0) + chargesTotal);
    }

    setBalanceDue(updatedBalance);

    if (updatedBalance > 0) {
      setPaymentAmount(updatedBalance);
    } else {
      setPaymentAmount(0);
    }
  }, [additionalCharges, reservation, isManagedByLandlord, invoiceDetails]);

  useEffect(() => {
    if (paymentMethod === 'Cash' && !receiptNumber) {
      const receiptNum = `RCT-${Date.now().toString().slice(-8)}`;
      setReceiptNumber(receiptNum);
    }
  }, [paymentMethod, receiptNumber]);

  const handleAddCharge = () => {
    if (!newCharge.description || newCharge.amount <= 0) {
      toastify("Please add a description and a valid amount for the charge", "error");
      return;
    }

    setAdditionalCharges([...additionalCharges, { ...newCharge, id: Date.now() }]);
    setNewCharge({ description: '', amount: 0, reason: '' });
  };

  const handleRemoveCharge = (id) => {
    setAdditionalCharges(additionalCharges.filter(charge => charge.id !== id));
  };

  const syncInvoiceCharges = async (charges) => {
    if (!reservation || isManagedByLandlord) return;

    const invoiceLookupId = reservation.invoiceId || reservation.invoiceNumber || reservation.invoice?.invoiceId;
    if (!invoiceLookupId) return;

    try {
      const facilityId = await getItem("selectedFacilityId");
      const chargesTotal = charges.reduce((sum, charge) => sum + (Number(charge.amount) || 0), 0);

      if (chargesTotal === additionalChargesTotal) {
        return;
      }

      await makeRequest2(
        `${checkoutReservationURL}/${facilityId}/${reservation._id}`,
        "POST",
        {
          checkoutNotes,
          checkOutActual: checkoutDate.toISOString(),
          additionalCharges: charges.map(({ id, ...rest }) => rest),
          syncOnly: true
        }
      );

      setAdditionalChargesTotal(chargesTotal);

      const response = await makeRequest2(
        `${GetBookingInvoiceDetailsURL}/${facilityId}/${invoiceLookupId}`,
        "GET"
      );

      if (response.success) {
        setInvoiceDetails(response.data?.data || response.data);
      }
    } catch (error) {
      console.error("Error syncing invoice charges:", error);
    }
  };

  useEffect(() => {
    if (!visible || isManagedByLandlord || !chargesInitialized) return;
    syncInvoiceCharges(additionalCharges);
  }, [additionalCharges, chargesInitialized, visible, isManagedByLandlord]);

  const handleSubmit = async () => {
    if (!reservation || !reservation._id) {
      toastify("No reservation selected for checkout", "error");
      return;
    }

    setSubmitting(true);

    try {
      const facilityId = await getItem("selectedFacilityId");

      if (!isManagedByLandlord && !invoiceDetails) {
        toastify("Invoice details are still loading. Please try again.", "error");
        setSubmitting(false);
        return;
      }

      const activeReservation = currentReservation || reservation;

      if (activeReservation.status && activeReservation.status !== 'checked-in') {
        toastify("Only checked-in reservations can be checked out.", "error");
        setSubmitting(false);
        return;
      }

      const { updatedBalance } = computeBalanceDue();

      if (updatedBalance > 0) {
        if (!paymentMethod) {
          toastify("Please select a payment method to complete checkout.", "error");
          setSubmitting(false);
          return;
        }
        if (!updatedBalance || updatedBalance <= 0) {
          toastify("Balance is not ready yet. Please try again.", "error");
          setSubmitting(false);
          return;
        }
        if (!paymentAmount || paymentAmount < updatedBalance) {
          toastify("Full payment is required before checkout.", "error");
          setSubmitting(false);
          return;
        }
        if (paymentMethod === 'Cash' && !receiptNumber) {
          toastify("Receipt number is required for cash payments.", "error");
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        checkoutNotes,
        checkOutActual: checkoutDate.toISOString(),
        additionalCharges: additionalCharges.map(({ id, ...rest }) => rest),
        paymentMethod: updatedBalance > 0 ? paymentMethod : undefined,
        paymentAmount: updatedBalance > 0 ? updatedBalance : undefined,
        paymentNotes: paymentNotes || undefined,
        receiptNumber: paymentMethod === 'Cash' ? receiptNumber : undefined,
        paymentDate: paymentDate ? paymentDate.toISOString() : undefined
      };

      // Add finalAmount only for property manager managed apartments
      if (!isManagedByLandlord) {
        payload.finalAmount = finalAmount;
      }

      console.log("Checking out reservation with payload:", payload);

      // Perform the checkout
      const response = await makeRequest2(
        `${checkoutReservationURL}/${facilityId}/${reservation._id}`,
        "POST",
        payload
      );

      console.log("Checkout API response:", response);

      if (response.success) {
        if (!response.data?.pendingApproval) {
          reservationTrendService.updateTrendData(new Date(), 'completed');
        }

        // Record checkout for revenue calculation only for property manager managed apartments
        if (!isManagedByLandlord && !response.data?.pendingApproval) {
          console.log("Recording checkout for revenue tracking...");
          try {
            await makeRequest2(
              `${record_checkout_for_revenue}/${facilityId}`,
              "POST",
              {
                checkoutDate: checkoutDate.toISOString(),
                finalAmount: finalAmount,
                reservationId: reservation._id,
                additionalCharges: additionalCharges.map(({ id, ...rest }) => rest)
              }
            );
          } catch (revenueError) {
            console.error("Error recording revenue, but checkout successful:", revenueError);
            // Don't fail checkout if revenue recording fails
          }
        }

        const message = response.data?.pendingApproval
          ? "Checkout pending finance approval. The reservation will complete once payment is approved."
          : isManagedByLandlord
            ? "Guest checked out successfully (Landlord Managed)"
            : "Guest checked out successfully";

        toastify(message, response.data?.pendingApproval ? "info" : "success");

        // Reset form
        setCheckoutNotes('');
        setAdditionalCharges([]);
        setNewCharge({
          description: '',
          amount: 0,
          reason: ''
        });
        setPaymentMethod('');
        setPaymentAmount(0);
        setPaymentNotes('');
        setReceiptNumber('');

        // Close modal
        onHide();

        // Notify parent component of success
        if (onSuccess && typeof onSuccess === 'function') {
          const successData = {
            ...response.data,
            checkoutDate: checkoutDate,
            additionalCharges: additionalCharges.map(({ id, ...rest }) => rest),
            managedByLandlord: isManagedByLandlord
          };

          // Add financial data only for property manager
          if (!isManagedByLandlord) {
            successData.finalAmount = finalAmount;
          }

          onSuccess(successData);
        }
      } else {
        console.error("Error during checkout:", response.error);
        toastify(response.error || "Failed to checkout guest", "error");
      }
    } catch (error) {
      console.error("Unexpected error during checkout:", error);
      toastify("An unexpected error occurred during checkout", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Format currency (only for property manager)
  const formatCurrency = (amount) => {
    if (isManagedByLandlord) {
      return 'N/A'; // No currency formatting for landlord managed
    }

    if (amount === undefined || amount === null) {
      return '$0.00';
    }

    const currencySymbol = reservation?.currencyId?.symbol ||
      reservation?.currency?.symbol ||
      reservation?.currencyId?.currencyShortCode ||
      '$';

    return `${currencySymbol}${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Action template for the DataTable
  const actionTemplate = (rowData) => {
    return (
      <Button
        icon="ti ti-trash"
        className="p-button-rounded p-button-danger p-button-sm"
        onClick={() => handleRemoveCharge(rowData.id)}
      />
    );
  };

  // If no reservation is selected
  if (!reservation) {
    return (
      <Dialog
        header="Checkout Error"
        visible={visible}
        onHide={onHide}
        style={{ width: '400px' }}
      >
        <div className="p-3 text-center">
          <i className="ti ti-alert-triangle text-warning" style={{ fontSize: '3rem' }}></i>
          <h5 className="mt-3">No Reservation Selected</h5>
          <p>Please select a reservation to checkout.</p>
          <Button label="Close" icon="ti ti-x" className="p-button-primary mt-3" onClick={onHide} />
        </div>
      </Dialog>
    );
  }

  // Get guest name
  const displayReservation = currentReservation || reservation;
  const guestName = displayReservation?.guestInfo?.name || 'Guest';
  const currencyCode = invoiceDetails?.currencyDetails?.code ||
    displayReservation?.currencyId?.currencyShortCode ||
    displayReservation?.currency?.currencyShortCode ||
    '$';

  const paymentMethodOptions = [
    { label: 'Cash', value: 'Cash' },
    { label: 'Card', value: 'Card' },
    { label: 'Bank Transfer', value: 'Bank Transfer' },
    { label: 'Mobile Money', value: 'Mobile Money' },
    { label: 'Other', value: 'Other' }
  ];

  const handleMpesaSuccess = () => {
    setMpesaModalVisible(false);
    toastify("M-Pesa payment received. Refreshing balance...", "success");
    setInvoiceRefreshKey((key) => key + 1);
    refreshReservation();
  };

  const handleMpesaError = (error) => {
    toastify(error?.message || "M-Pesa payment failed. Please try again.", "error");
  };

  const handleRecordPaymentSuccess = () => {
    setRecordPaymentModalVisible(false);
    setInvoiceRefreshKey((key) => key + 1);
    refreshReservation();
  };

  // While a balance remains, checkout is not possible at all (the backend
  // hard-blocks it) - so this button shouldn't even attempt it. Clicking
  // "Await Payment" just closes the dialog and leaves the guest checked-in
  // until the balance is settled via Pay Now/Record Payment above; only
  // once the balance clears does this become a real checkout action.
  const canActuallyCheckout = isManagedByLandlord || balanceDue <= 0;
  const checkoutButtonLabel = canActuallyCheckout ? 'Checkout' : 'Await Payment';

  return (
    <>
    <Dialog
      header={`Checkout: ${guestName} ${isManagedByLandlord ? '(Landlord Managed)' : ''}`}
      visible={visible}
      style={{ width: '700px' }}
      onHide={onHide}
      footer={
        <div className="d-flex justify-content-end gap-2">
          <Button
            label="Cancel"
            icon="ti ti-x"
            className="p-button-text"
            onClick={onHide}
            disabled={submitting}
          />
          <Button
            label={checkoutButtonLabel}
            icon="ti ti-check"
            className="p-button-primary"
            onClick={canActuallyCheckout ? handleSubmit : onHide}
            loading={submitting}
          />
        </div>
      }
    >
      <div className="p-fluid">
        {/* Management Type Indicator */}
        {isManagedByLandlord && (
          <div className="alert alert-info mb-3">
            <i className="ti ti-info-circle me-2"></i>
            <strong>Landlord Managed Apartment:</strong> Only additional charges and notes will be recorded.
          </div>
        )}

        {/* Reservation Summary */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body p-3 bg-light rounded">
                <h6 className="card-title">Reservation Details</h6>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-2">
                      <small className="text-muted">Booking ID:</small>
                      <div>{displayReservation?.bookingReservationId || 'N/A'}</div>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Guest:</small>
                      <div>{guestName}</div>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Apartment:</small>
                      <div>{displayReservation?.unitId?.name || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <small className="text-muted">Check-in:</small>
                      <div>{displayReservation?.checkIn ? new Date(displayReservation.checkIn).toLocaleDateString() : 'N/A'}</div>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Check-out:</small>
                      <div>{displayReservation?.checkOut ? new Date(displayReservation.checkOut).toLocaleDateString() : 'N/A'}</div>
                    </div>
                    {!isManagedByLandlord && (
                      <div className="mb-2">
                        <small className="text-muted">Original Amount:</small>
                        <div>{formatCurrency(displayReservation?.totalAmount)}</div>
                      </div>
                    )}
                    {!isManagedByLandlord && invoiceDetails && (
                      <div className="mb-2">
                        <small className="text-muted">Current Balance:</small>
                        <div>{formatCurrency(balanceDue)}</div>
                      </div>
                    )}
                    {isManagedByLandlord && (
                      <div className="mb-2">
                        <small className="text-muted">Management:</small>
                        <div>Landlord Managed</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Charges */}
        <div className="row mb-3">
          <div className="col-12">
            <h6 className="mb-2">Additional Charges</h6>

            {/* Input form for new charge */}
            <div className="row mb-2">
              <div className="col-md-4">
                <label className="form-label">Description</label>
                <InputText
                  value={newCharge.description}
                  onChange={(e) => setNewCharge({ ...newCharge, description: e.target.value })}
                  placeholder="e.g., Minibar, Damage, Service"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Amount</label>
                <InputNumber
                  value={newCharge.amount}
                  onChange={(e) => setNewCharge({ ...newCharge, amount: e.value })}
                  mode="decimal"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  placeholder="0.00"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Reason (Optional)</label>
                <InputText
                  value={newCharge.reason}
                  onChange={(e) => setNewCharge({ ...newCharge, reason: e.target.value })}
                  placeholder="Reason"
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <Button
                  label="Add"
                  icon="ti ti-plus"
                  className="p-button-success w-100"
                  onClick={handleAddCharge}
                />
              </div>
            </div>

            {/* Table of additional charges */}
            {additionalCharges.length > 0 ? (
              <DataTable value={additionalCharges} className="p-datatable-sm" responsiveLayout="scroll">
                <Column field="description" header="Description" />
                <Column
                  field="amount"
                  header="Amount"
                  body={(rowData) => {
                    if (isManagedByLandlord) {
                      return `${rowData.amount.toFixed(2)}`;
                    }
                    return formatCurrency(rowData.amount);
                  }}
                />
                <Column field="reason" header="Reason" />
                <Column body={actionTemplate} header="Actions" style={{ width: '80px' }} />
              </DataTable>
            ) : (
              <div className="text-center py-4 bg-light rounded">
                <i className="ti ti-info-circle me-2"></i>
                No additional charges added
              </div>
            )}
          </div>
        </div>

        {/* Checkout notes */}
        <div className="row mb-3">
          <div className="col-12">
            <label className="form-label">Checkout Notes</label>
            <InputTextarea
              value={checkoutNotes}
              onChange={(e) => setCheckoutNotes(e.target.value)}
              rows={5}
              placeholder="Enter any notes about the checkout process (e.g., property condition, damages, extra cleaning required, guest feedback, etc.)"
              className="w-100"
            />
            <small className="text-muted d-block mt-1">
              Document property condition, any issues found, or special circumstances during checkout
            </small>
          </div>
        </div>

        {/* Payment Section */}
        {!isManagedByLandlord && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="card">
                <div className="card-body p-3 bg-light rounded">
                  <h6 className="card-title">Payment</h6>
                  {invoiceLoading ? (
                    <div className="text-muted">Loading invoice details...</div>
                  ) : balanceDue > 0 ? (
                    <>
                      <div className="row">
                        <div className="col-12 mb-3">
                          <small className="text-muted d-block mt-1">
                            Balance due: {currencyCode} {balanceDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </small>
                          {(() => {
                            const { chargesTotal, invoiceBalance, updatedBalance } = computeBalanceDue();
                            return (
                              <div className="mt-2 small text-muted">
                                <div className="d-flex justify-content-between">
                                  <span>Invoice balance</span>
                                  <span>{currencyCode} {invoiceBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <span>Additional charges</span>
                                  <span>{currencyCode} {chargesTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div className="d-flex justify-content-between fw-semibold text-dark">
                                  <span>Total due</span>
                                  <span>{currencyCode} {updatedBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Settle the outstanding balance immediately, before completing checkout */}
                      <div className="d-flex gap-2 flex-wrap mb-3">
                        <Button
                          label="Pay Now (M-Pesa)"
                          icon="ti ti-device-mobile"
                          className="p-button-success"
                          disabled={!invoiceDetails?.accountNumber}
                          onClick={() => setMpesaModalVisible(true)}
                        />
                        <Button
                          label="Record Payment"
                          icon="ti ti-cash"
                          className="p-button-outlined"
                          onClick={() => setRecordPaymentModalVisible(true)}
                        />
                      </div>
                      {!invoiceDetails?.accountNumber && (
                        <small className="text-danger d-block mb-2">
                          This invoice has no M-Pesa account registered yet - use Record Payment instead.
                        </small>
                      )}

                      <div className="border-top pt-3 mt-2">
                        <small className="text-muted d-block mb-2">
                          Or settle the balance while completing checkout:
                        </small>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Payment Method <span className="text-danger">*</span></label>
                            <Dropdown
                              value={paymentMethod}
                              options={paymentMethodOptions}
                              onChange={(e) => setPaymentMethod(e.value)}
                              placeholder="Select payment method"
                              className="w-100"
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Amount Due <span className="text-danger">*</span></label>
                            <InputNumber
                              value={paymentAmount}
                              onValueChange={(e) => setPaymentAmount(e.value || 0)}
                              mode="decimal"
                              minFractionDigits={2}
                              maxFractionDigits={2}
                              min={0}
                              className="w-100"
                              placeholder="Enter payment amount"
                              disabled
                            />
                          </div>
                        </div>
                        {paymentMethod === 'Cash' && (
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Receipt Number</label>
                              <InputText
                                value={receiptNumber}
                                readOnly
                                className="w-100"
                                placeholder="Auto-generated"
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Payment Notes (optional)</label>
                              <InputText
                                value={paymentNotes}
                                onChange={(e) => setPaymentNotes(e.target.value)}
                                className="w-100"
                                placeholder="Add payment notes"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-success">Invoice is fully paid.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Final Total - Only for Property Manager */}
        {!isManagedByLandlord && (
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                <h5 className="mb-0">Final Amount:</h5>
                <h5 className="mb-0 text-success">{formatCurrency(finalAmount)}</h5>
              </div>
            </div>
          </div>
        )}

        {/* Simple Total for Landlord Managed */}
        {isManagedByLandlord && additionalCharges.length > 0 && (
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                <h5 className="mb-0">Additional Charges Total:</h5>
                <h5 className="mb-0 text-primary">{finalAmount.toFixed(2)}</h5>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
    <PaymentModal
      visible={mpesaModalVisible}
      onHide={() => setMpesaModalVisible(false)}
      accountNumber={invoiceDetails?.accountNumber}
      facilityId={checkoutFacilityId}
      customerId={null}
      amount={balanceDue}
      phoneNumber={displayReservation?.guestInfo?.phone}
      onSuccess={handleMpesaSuccess}
      onError={handleMpesaError}
    />
    <RecordPaymentDialog
      visible={recordPaymentModalVisible}
      onHide={() => setRecordPaymentModalVisible(false)}
      invoice={invoiceDetails}
      onPaymentSuccess={handleRecordPaymentSuccess}
    />
    </>
  );
};

export default CheckoutModal;
